import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [favoriteFarms, setFavoriteFarms] = useState([]);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [favoriteMarkets, setFavoriteMarkets] = useState([]);
  const [favoritePlaces, setFavoritePlaces] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all favorites when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllFavorites();
    } else {
      // Clear favorites on logout
      setFavoriteFarms([]);
      setFavoriteProducts([]);
      setFavoriteMarkets([]);
      setFavoritePlaces([]);
    }
  }, [isAuthenticated]);

  const fetchAllFavorites = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    try {
      const [farmsRes, productsRes, marketsRes, placesRes] = await Promise.allSettled([
        axios.get(`${API}/favorites/farms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/favorites/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/favorites/markets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/favorites/places`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (placesRes.status === "fulfilled") {
        setFavoritePlaces(placesRes.value.data.favorites || []);
      }

      if (farmsRes.status === "fulfilled") {
        setFavoriteFarms(farmsRes.value.data.favorites || []);
      }
      if (productsRes.status === "fulfilled") {
        setFavoriteProducts(productsRes.value.data.favorites || []);
      }
      if (marketsRes.status === "fulfilled") {
        setFavoriteMarkets(marketsRes.value.data.favorites || []);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle favorite for farms
  const toggleFavoriteFarm = useCallback(async (farmId, farmData = null) => {
    if (!isAuthenticated) {
      return { success: false, requiresAuth: true };
    }
    const token = localStorage.getItem("token");
    const isFavorite = favoriteFarms.some(f => f.id === farmId);

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/farms/${farmId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteFarms(prev => prev.filter(f => f.id !== farmId));
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites/farms/${farmId}`, farmData || {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteFarms(prev => [...prev, { id: farmId, ...farmData }]);
        toast.success("Added to favorites");
      }
      return { success: true };
    } catch (error) {
      console.error("Error toggling farm favorite:", error);
      toast.error("Could not update favorite");
      return { success: false };
    }
  }, [isAuthenticated, favoriteFarms]);

  // Toggle saved directory places (farms / farmstands) — free accounts
  const toggleFavoritePlace = useCallback(async (place) => {
    if (!isAuthenticated) return { success: false, requiresAuth: true };
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const isFavorite = favoritePlaces.some(p => p.id === place.id);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/places/${encodeURIComponent(place.id)}`, { headers });
        setFavoritePlaces(prev => prev.filter(p => p.id !== place.id));
        toast.success("Removed from Saved Places");
      } else {
        await axios.post(`${API}/favorites/places/${encodeURIComponent(place.id)}`, {}, { headers });
        setFavoritePlaces(prev => [...prev, place]);
        toast.success("Saved to your places");
      }
      return { success: true };
    } catch (error) {
      toast.error("Could not update Saved Places");
      return { success: false };
    }
  }, [isAuthenticated, favoritePlaces]);

  const isPlaceFavorite = useCallback((id) => favoritePlaces.some(p => p.id === id), [favoritePlaces]);

  // Toggle favorite for products (optimistic; rolls back on failure; one request per product at a time)
  const pendingProducts = useRef(new Set());
  const toggleFavoriteProduct = useCallback(async (productId, productData = null) => {
    if (!isAuthenticated) {
      return { success: false, requiresAuth: true };
    }
    if (pendingProducts.current.has(productId)) return { success: false, pending: true };
    pendingProducts.current.add(productId);
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const isFavorite = favoriteProducts.some(p => p.id === productId);
    const snapshot = favoriteProducts;
    setFavoriteProducts(prev => isFavorite ? prev.filter(p => p.id !== productId)
      : [...prev.filter(p => p.id !== productId), { id: productId, available: true, ...productData }]);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/products/${encodeURIComponent(productId)}`, { headers });
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites/products/${encodeURIComponent(productId)}`, {}, { headers });
        toast.success("Added to favorites");
      }
      return { success: true };
    } catch (error) {
      setFavoriteProducts(snapshot);
      const status = error?.response?.status;
      toast.error(status === 404 ? "This product isn't available to save from your account" : "Could not update favorite");
      return { success: false };
    } finally {
      pendingProducts.current.delete(productId);
    }
  }, [isAuthenticated, favoriteProducts]);

  // Toggle favorite for markets
  const toggleFavoriteMarket = useCallback(async (marketId, marketData = null) => {
    if (!isAuthenticated) {
      return { success: false, requiresAuth: true };
    }

    const token = localStorage.getItem("token");
    const isFavorite = favoriteMarkets.some(m => m.id === marketId);

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/markets/${marketId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteMarkets(prev => prev.filter(m => m.id !== marketId));
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites/markets/${marketId}`, marketData || {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteMarkets(prev => [...prev, { id: marketId, ...marketData }]);
        toast.success("Added to favorites");
      }
      return { success: true };
    } catch (error) {
      console.error("Error toggling market favorite:", error);
      toast.error("Could not update favorite");
      return { success: false };
    }
  }, [isAuthenticated, favoriteMarkets]);

  // Check if item is favorited
  const isFarmFavorite = useCallback((farmId) => {
    return favoriteFarms.some(f => f.id === farmId);
  }, [favoriteFarms]);

  const isProductFavorite = useCallback((productId) => {
    return favoriteProducts.some(p => p.id === productId);
  }, [favoriteProducts]);

  const isMarketFavorite = useCallback((marketId) => {
    return favoriteMarkets.some(m => m.id === marketId);
  }, [favoriteMarkets]);

  return (
    <FavoritesContext.Provider
      value={{
        favoriteFarms,
        favoriteProducts,
        favoriteMarkets,
        favoritePlaces,
        toggleFavoritePlace,
        isPlaceFavorite,
        loading,
        toggleFavoriteFarm,
        toggleFavoriteProduct,
        toggleFavoriteMarket,
        isFarmFavorite,
        isProductFavorite,
        isMarketFavorite,
        refetchFavorites: fetchAllFavorites
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within FavoritesProvider");
  }
  return context;
}
